import { useEffect, useState } from "react";
import { useSecureService } from "../../store/service";
import { useAuth } from "../../hooks/useAuth";
import MainLayout from "../../layouts/Layout";
import { ProjectOutlined, UserOutlined, CalendarOutlined, FilterOutlined, PlusOutlined } from "@ant-design/icons";
import { ReactComponent as ImagePlaceholder } from "../../images/image-placeholder.svg";
import styles from "./Projects.module.scss";
import { Link, Routes, Route, useNavigate } from "react-router-dom";
import FolderCard from "../../components/FolderCard/FolderCard";
import { Button, Select, DatePicker, message, Checkbox } from "antd";
import FileDetail from "./FileDetail";
import FooterActions from "./FooterActions";
import ApprovalModal from "../../modals/approvalModal";
import AddNewProjectModal from "../../modals/addNewProjectModal";
import { getStatusLabel, getStatusColor, statusEnumArray } from "../../utils/statusHelpers";
import  { Dayjs } from "dayjs";
import { useLanguage } from "../../contexts/LanguageContext";
import SmartRangePicker from "../../components/SmartRangePicker/SmartRangePicker";
import type { ApiProjectAllGetScopeEnum } from "../../service/apis/ProjectApi";
const formatDate = (value: string | Date | undefined | null) => {
  if (!value) return "-";

  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string') {
    const hasTimezone = /Z$|[\+\-]\d{2}:?\d{2}$/.test(value);
    const iso = hasTimezone ? value : value + 'Z';
    date = new Date(iso);
  } else {
    return "-";
  }
  
  if (isNaN(date.getTime())) return "-";

  return date.toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Efektif tarih: updatedAt varsa onu, yoksa createdAt
const getEffectiveDate = (project: any): Date | null => {
  const value = project?.updatedAt ?? project?.createdAt;
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const hasTimezone = /Z$|[\+\-]\d{2}:?\d{2}$/.test(value);
    const iso = hasTimezone ? value : value + 'Z';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

interface ProjectsProps {
  scope?: ApiProjectAllGetScopeEnum;
  pageTitle?: string;
}

const Projects: React.FC<ProjectsProps> = ({ scope = 'Internal', pageTitle = 'Internal Projects' }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { getProjects, getAllUserEmails, deleteEntry, changeStatus, getFileById } = useSecureService();
  const [projects, setProjects] = useState<any[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<any[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [, setSharedProjects] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | undefined>();
  const [creationDate, setCreationDate] = useState<[Dayjs, Dayjs] | null>(null);
  const [lastUpdate, setLastUpdate] = useState<[Dayjs, Dayjs] | null>(null);
  const [shareLoading, setShareLoading] = useState(false);

  // FooterActions için state'ler
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [selectedItemsDetails, setSelectedItemsDetails] = useState<Array<{ id: number; name: string; entryType: string; status?: string; share?: any[]; thumbnail?: any; path?: string; fileUrl?: string }>>([]);
  // Approval modal state'leri
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const projectsBasePath = scope === 'External' ? '/external-projects' : '/internal-projects';
  const canCreateProject = !!user && ['Admin', 'SuperAdmin', 'AccountOwner'].includes(user.role);

  useEffect(() => {
    localStorage.setItem('lastProjectsBasePath', projectsBasePath);
    const fetchProjects = async () => {
      const response = await getProjects(scope);
      setProjects(response?.data || []);
      setFilteredProjects(response?.data || []);
    };
    fetchProjects();
  }, [scope, projectsBasePath]);

  useEffect(() => {
    projects.forEach(project => {
      if(project.shared) {
        setSharedProjects(project);
      }
    });
  }, [projects]);

  const handleUserSearch = async (value: string) => {
    if (!value) {
      setUserOptions([]);
      return;
    }
    setShareLoading(true);
    const response = await getAllUserEmails(value);
    if (response.success && Array.isArray(response.data)) {
      setUserOptions(
        response.data.map((email: string) => ({
          label: email,
          value: email
        }))
      );
    } else {
      setUserOptions([]);
    }
    setShareLoading(false);
  };

  const applyAllFilters = (
    status = selectedStatus,
    user = selectedUser,
    creation = creationDate,
    update = lastUpdate
  ) => {
    let filtered = [...projects];

    // Statü filtresi
    if (status !== null) {
      filtered = filtered.filter(project => project.status === status);
    }

    // User filtresi
    if (user) {
      filtered = filtered.filter(project =>
        Array.isArray(project.share) &&
        project.share.some((u: any) => u.email === user)
      );
    }

    // Creation Date filtresi
    if (creation && creation[0] && creation[1]) {
      filtered = filtered.filter(project => {
        if (!project.createdAt) return false;
        
        // createdAt zaten Date object olarak geliyor
        const projectDate = project.createdAt instanceof Date ? project.createdAt : new Date(project.createdAt);
        
        // Seçilen tarih aralığını Date'e çevir
        const startDate = creation[0].startOf('day').toDate();
        const endDate = creation[1].endOf('day').toDate();
        
        return projectDate >= startDate && projectDate <= endDate;
      });
    }

    // Last Update filtresi
    if (update && update[0] && update[1]) {
      filtered = filtered.filter(project => {
        if (!project.updatedAt) return false;
        
        // updatedAt zaten Date object olarak geliyor
        const projectDate = project.updatedAt instanceof Date ? project.updatedAt : new Date(project.updatedAt);
        
        // Seçilen tarih aralığını Date'e çevir
        const startDate = update[0].startOf('day').toDate();
        const endDate = update[1].endOf('day').toDate();
        
        return projectDate >= startDate && projectDate <= endDate;
      });
    }

    setFilteredProjects(filtered);
  };

  const handleStatusFilter = (status: string | null) => {
    setSelectedStatus(status);
    applyAllFilters(status);
  };

  const handleUserChange = (user: string | undefined) => {
    setSelectedUser(user);
  };

  const handleCreationDateChange = (dates: any) => {
    setCreationDate(dates);
  };

  const handleLastUpdateChange = (dates: any) => {
    setLastUpdate(dates);
  };

  const handleFilter = () => {
    applyAllFilters(selectedStatus, selectedUser, creationDate, lastUpdate);
  };

  // FooterActions için handler fonksiyonları
  const handleDelete = async () => {
    try {
      if (selectedItems.size > 0) {
        // Seçili projeleri sil
        const entriesToDelete = Array.from(selectedItems).map(id => ({
          id,
          entryType: 'Project'
        }));
        await deleteEntry(entriesToDelete);
        message.success('Selected projects deleted successfully.');
        // Projeleri yeniden yükle
        const response = await getProjects(scope);
        setProjects(response?.data || []);
        setFilteredProjects(response?.data || []);
        setSelectedItems(new Set());
        setSelectedItemsDetails([]);
      }
    } catch (error) {
      message.error('An error occurred during deletion.');
    }
  };

  const handleDownload = async () => {
    if (!selectedItems || selectedItems.size === 0) {
      message.error('No file selected for download.');
      return;
    }

    // Sadece File tipindeki öğeleri filtrele
    const fileItems = selectedItemsDetails.filter((item: any) => item.entryType === 'File');
    
    if (fileItems.length === 0) {
      message.error('No downloadable files found in selection.');
      return;
    }

    try {
      for (const fileItem of fileItems) {
        try {
          // File detaylarını al
          const fileResponse = await getFileById(fileItem.id);
          
          if (fileResponse.success && fileResponse.data) {
            const fileDetail = fileResponse.data;
            
            // Versions'dan fileUrl'i al
            let downloadUrl = null;
            
            if (fileDetail.versions && fileDetail.versions.length > 0) {
              // En son versiyonun fileUrl'ini al
              const latestVersion = fileDetail.versions[fileDetail.versions.length - 1];
              downloadUrl = latestVersion.fileUrl;
            }
            
            if (downloadUrl) {
              const fileName = fileItem.name || 'download';
              const fileExtension = downloadUrl.split('.').pop() || 'mp4';
              const fullFileName = `${fileName}.${fileExtension}`;
              
              const response = await fetch(downloadUrl);
              if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                
                const link = document.createElement('a');
                link.href = url;
                link.download = fullFileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                window.URL.revokeObjectURL(url);
                
                message.success(`${fullFileName} download started.`);
              } else {
                message.error(`Failed to download ${fileName}.`);
              }
            } else {
              message.error(`Download URL not available for ${fileItem.name}.`);
            }
          } else {
            message.error(`Failed to get file details for ${fileItem.name}.`);
          }
        } catch (fileError) {
          message.error(`Error downloading ${fileItem.name}.`);
          console.error('File download error:', fileError);
        }
      }
    } catch (error) {
      message.error('An error occurred during download.');
      console.error('Download error:', error);
    }
  };

  const handleCopyUrl = () => {
    // Seçili item'ların URL'lerini kopyala
    let urlsToCopy = '';
    
    if (selectedItems && selectedItems.size > 0) {
      // Seçili item'ların URL'lerini oluştur
      const baseUrl = window.location.origin;
      urlsToCopy = selectedItemsDetails?.map(item => {
        if (item.entryType === 'Project') {
          return `${baseUrl}/${item.path}`;
        } else if (item.entryType === 'File') {
          return `${baseUrl}/${item.path}`;
        } else if (item.entryType === 'Folder') {
          return `${baseUrl}/${item.path}`;
        }
        return '';
      }).filter(url => url !== '').join('\n');
    } else {
      // Hiçbir şey seçili değilse mevcut URL'i kopyala
      urlsToCopy = window.location.href;
    }
    
    navigator.clipboard.writeText(urlsToCopy);
          message.success('URL copied.');
  };

  const handleCompare = () => {
    // Compare işlemi - şimdilik boş
  };

  // Efektif tarihe göre (updatedAt varsa o, yoksa createdAt) azalan sıralama
  const projectsToRender = [...filteredProjects].sort((a, b) => {
    const da = getEffectiveDate(a);
    const db = getEffectiveDate(b);
    const ta = da ? da.getTime() : 0;
    const tb = db ? db.getTime() : 0;
    return tb - ta;
  });

  // Approval modal fonksiyonları
  const handleApprovalConfirm = async (changeSubentries: boolean) => {
    setApprovalLoading(true);
    try {
      // Seçili projelerin statüsünü "Approved" olarak değiştir
      if (selectedItems.size > 0) {
        const entriesToChange = selectedItemsDetails.map(item => ({
          id: item.id,
          entryType: item.entryType,
          entryStatus: 'Approved',
          changeSubentries: changeSubentries
        }));
        
        // changeStatus API'sini çağır
        const response = await changeStatus(entriesToChange);
        
        if (response.success) {
          message.success('Status changed successfully.');
          // Projeleri yeniden yükle
          const projectsResponse = await getProjects(scope);
          setProjects(projectsResponse?.data || []);
          setFilteredProjects(projectsResponse?.data || []);
          setSelectedItems(new Set());
          setSelectedItemsDetails([]);
        } else {
          throw new Error(response.message || 'Status change failed');
        }
      }
      
      // Modal'ı kapat
      setIsApprovalModalVisible(false);
    } catch (error) {
      message.error('Failed to change status. Please try again.');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleApprovalCancel = () => {
    setIsApprovalModalVisible(false);
  };

  // Toplu seçim yetkisi kontrolü - sadece admin, super admin ve account owner
  const canMultiSelect = () => {
    if (!user) return false;
    return ['Admin', 'SuperAdmin', 'AccountOwner'].includes(user.role);
  };

  // Proje seçimi için handler
  const handleProjectSelect = (project: any, isSelected: boolean) => {
    if (isSelected) {
      // Toplu seçim kontrolü - sadece yetkili roller birden fazla seçim yapabilir
      if (selectedItems.size > 0 && !canMultiSelect()) {
        // Yetkisiz kullanıcı zaten bir öğe seçiliyse yeni seçim yapamaz
        message.warning('You can only make single selections. You do not have permission for multi-selection.');
        return;
      }
      
      setSelectedItems(prev => new Set([...Array.from(prev), project.id]));
      setSelectedItemsDetails(prev => [...prev, {
        id: project.id,
        name: project.name,
        entryType: 'Project',
        status: project.status,
        share: project.share,
        thumbnail: project.thumbnail,
        path: project.path
      }]);
    } else {
      setSelectedItems(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(project.id);
        return newSet;
      });
      setSelectedItemsDetails(prev => prev.filter(item => item.id !== project.id));
    }
  };

  return (
    <Routes>
      <Route path=":id/file/:fileId" element={<FileDetail breadcrumbPathParts={[]} bclocation={null} />} />
      <Route path="" element={
        <MainLayout
          title={pageTitle || t('projects.title')}
          icon={<ProjectOutlined />}
          headerActions={canCreateProject ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsProjectModalOpen(true)}>
              New Project
            </Button>
          ) : undefined}
        >
          {/* <div className={styles.filterContainer}>
            <Button
              type="text"
              onClick={() => handleStatusFilter(null)}
              className={`${styles.statusFilterButton} ${selectedStatus === null ? styles.active : ''}`}
            >
              {t('common.all')}
            </Button>
            {statusEnumArray.map((status: string) => (
              <Button
                key={status}
                type="text"
                onClick={() => handleStatusFilter(status)}
                className={`${styles.statusFilterButton} ${selectedStatus === status ? styles.statusActive : ''}`}
                style={{
                  color: getStatusColor(status),
                  borderTopColor: selectedStatus === status ? getStatusColor(status) : 'transparent'
                }}
              >
                {t(`projects.statusOptions.${status.toLowerCase()}`)}
              </Button>
            ))}
          </div> */}
          <div className={styles.advancedFilterRow}>
            {/* Users Filter - Guest için gizli */}
            {user?.role !== 'Guest' && (
            <div className={styles.inputContainer}>
              <label className={styles.inputLabel}>{t('projects.users')}</label>
            <Select
              showSearch
              allowClear
              className={styles.selectInput}
              value={selectedUser}
              onChange={handleUserChange}
              onSearch={handleUserSearch}
              filterOption={false}
              loading={shareLoading}
                notFoundContent={shareLoading ? t('common.loading') : t('common.noResults')}
              dropdownStyle={{ zIndex: 9999 }}
              prefix={<UserOutlined style={{ color: "var(--app-accent-color)" }} />}
            >
              {userOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            </div>
            )}
            <div className={styles.inputContainer}>
              <label className={styles.inputLabel}>{t('projects.creationDate')}</label>
              <SmartRangePicker
              className={`${styles.selectInput} ${styles.datePicker}`}
              allowClear
              value={creationDate}
              prefix={<CalendarOutlined style={{ color: "var(--app-accent-color)" }} />}  
              onChange={handleCreationDateChange}
                format="DD.MM.YYYY"
                placeholder={["", ""]}
            />
            </div>
            <div className={styles.inputContainer}>
              <label className={styles.inputLabel}>{t('projects.lastUpdate')}</label>
              <SmartRangePicker
              className={`${styles.selectInput} ${styles.datePicker}`}
              allowClear
              value={lastUpdate}
              prefix={<CalendarOutlined style={{ color: "var(--app-accent-color)" }} />}  
              onChange={handleLastUpdateChange}
                format="DD.MM.YYYY"
                placeholder={["", ""]}
            />
            </div>
            {/* Guest için filter butonu input'larla aynı seviyede */}
            {user?.role === 'Guest' && (
              <div className={styles.guestFilterContainer}>
                <div className={styles.spacer}></div>
                <div className={styles.buttonWrapper}>
                  <Button
                    type="primary"
                    icon={<FilterOutlined />}
                    className={styles.filterButton}
                    onClick={handleFilter}
                  >
                    {t('common.filter')}
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {/* Diğer roller için filter butonu normal yerde */}
          {user?.role !== 'Guest' && (
            <div className={styles.filterButtonRow}>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                className={styles.filterButton}
                onClick={handleFilter}
              >
                {t('common.filter')}
              </Button>
            </div>
          )}
          <div className={styles.projectGrid}>
            {projectsToRender.map((project) => (
              <div key={project.id} className={styles.projectGridItem}>
                {user?.role !== 'BrandAccountViewer' && (
                <Checkbox
                  checked={selectedItems.has(project.id)}
                  onChange={(e) => handleProjectSelect(project, e.target.checked)}
                  disabled={selectedItems.size > 0 && !selectedItems.has(project.id) && !canMultiSelect()}
                  className={styles.projectCheckbox}
                />
                )}
              <Link 
                to={`/${project.path}`} 
                state={{ fromProjectsBasePath: projectsBasePath }}
                className={styles.projectLink}
              >
                  <FolderCard 
                    className={styles.projectCard}
                    folderType={project.entryType === 'File' ? 'file' : 'folder'}
                    sharedUsers={project.share || []}
                  >
                  <div className={styles.gradientBorderBox}>
                    <div className={styles.gradientBorderBoxContent}>
                      {project.name}
                    </div>
                  </div>
                  <div className={styles.projectImage}>
                    {project.thumbnailUrl ? <img src={project.thumbnailUrl} alt={project.name} /> : <ImagePlaceholder style={{ width: "100%", height: "100%" }} />}
                  </div>
                  <div className={styles.projectDetailINfo}>
                    {/* File entry type için status göster, Project ve Folder için gösterme */}
                    {project.entryType === 'File' && (
                      <div className={styles.projectStatusContainer}>
                        <div className={styles.projectStatus} style={{ 
                          backgroundColor: getStatusColor(project.versions?.[0]?.status || project.status)
                        }}>
                          {getStatusLabel(project.versions?.[0]?.status || project.status)}
                        </div>
                      </div>
                    )}
                    
                    {/* Share Avatar'ları */}
                    {project.share && project.share.length > 0 && (
                      <div className={styles.shareAvatarsContainer}>
                        <div className={styles.shareAvatarsWrapper}>
                          {project.share.slice(0, 3).map((shareItem: any, idx: number) => (
                            <div
                              key={shareItem.id || idx}
                              className={styles.shareAvatar}
                              style={{
                                backgroundColor: shareItem.thumbnailUrl ? 'transparent' : "var(--app-accent-color)",
                                marginLeft: idx > 0 ? -8 : 0,
                                zIndex: 3 - idx,
                              }}
                            >
                              {shareItem.thumbnailUrl ? (
                                <img 
                                  src={shareItem.thumbnailUrl} 
                                  alt={`${shareItem.firstName} ${shareItem.lastName}`}
                                  style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                                />
                              ) : (
                                <span style={{ 
                                  color: '#fff', 
                                  fontWeight: 700, 
                                  fontSize: 14 
                                }}>
                                  {(shareItem.firstName?.[0] || shareItem.email?.[0] || 'U')?.toUpperCase()}
                                </span>
                              )}
                            </div>
                          ))}
                          {project.share.length > 3 && (
                            <div className={styles.shareAvatarMore}>
                              +{project.share.length - 3}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={styles.projectFooter}>
                    <div className={styles.gradientBorderTop}>
                      <span className={styles.date}>{formatDate(getEffectiveDate(project))}</span>
                    </div>
                  </div>
                </FolderCard>
              </Link>
              </div>
            ))}
          </div>
          
          {/* FooterActions - Sadece seçim yapıldığında göster */}
          {selectedItems.size > 0 && (
            <FooterActions
              onDelete={handleDelete}
              onDownload={handleDownload}
              onCopyUrl={handleCopyUrl}
              onCompare={handleCompare}
              onApprovalRequest={() => setIsApprovalModalVisible(true)} // Approval modal'ı aç
              selectedItems={selectedItems}
              selectedItemsDetails={selectedItemsDetails}
              style={{
                transform: 'translateY(0)',
                transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
                opacity: 1
              }}
            />
          )}
          
          {/* Footer Actions için safe alan */}
          <div className={styles.footerSafeArea} />
          
          {/* Approval Modal */}
          <ApprovalModal
            visible={isApprovalModalVisible}
            onCancel={handleApprovalCancel}
            onConfirm={handleApprovalConfirm}
            loading={approvalLoading}
            hasSubentries={selectedItemsDetails.some(item => {
              // Project'lerin her zaman alt öğesi olabilir
              if (item.entryType === 'Project') return true;
              // Folder'ların da alt öğesi olabilir
              if (item.entryType === 'Folder') return true;
              // File'ların versiyonları olabilir (bu kontrol API'den gelecek data'ya bağlı)
              if (item.entryType === 'File') return true;
              return false;
            })}
          />

          <AddNewProjectModal
            isOpen={isProjectModalOpen}
            onClose={() => setIsProjectModalOpen(false)}
            defaultScope={scope}
            onSuccess={(path) => {
              setIsProjectModalOpen(false);
              if (path) {
                navigate(`/${path}`, { state: { fromProjectsBasePath: projectsBasePath } });
                return;
              }
              getProjects(scope).then((response) => {
                setProjects(response?.data || []);
                setFilteredProjects(response?.data || []);
              });
            }}
          />
        </MainLayout>
      } />
    </Routes>
  );
};

export default Projects;